import { IPushesViewContext, PushesViewContext } from '../../pushesViewContext/pushesViewState/pushesViewContext';
import React, { useContext, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IRestaurantFullInfoContext,
	RestaurantFullInfoContext,
} from 'modules/CustomersAndRestaurants/Restaurants/RestaurantFullInfo/restaurantFullInfoState/restaurantFullInfoContext';
import { RestaurantNotificationEventType, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { forceReloadPushEvents } from 'appConstants';
import { pushRestaurantNotification } from 'backend/restaurants';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import { PushOptionCheckbox } from './PushOptionCheckbox/PushOptionCheckbox';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantForFullInfo } = useContext<IRestaurantFullInfoContext>(RestaurantFullInfoContext);
	const { changeTypeOfModalWindow, setPushEventType, pushEventType, typeOfModalWindow } =
		useContext<IPushesViewContext>(PushesViewContext);
	const hasSuperUserAccess = useRoleAccess([UserRoles.SUPER_USER]);
	const [forceReload, setForceReload] = useState<boolean>(false);
	const [useDefaultTimeout, setUseDefaultTimeout] = useState<boolean>(true);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow });

	const isSyncMenuPush = pushEventType === RestaurantNotificationEventType.SynchronizationMenu;
	const isForceReloadAvailable =
		hasSuperUserAccess && pushEventType !== null && forceReloadPushEvents.includes(pushEventType);
	const isExtendedTimeoutAvailable = hasSuperUserAccess && isSyncMenuPush;

	const sendPush = async () => {
		if (restaurantForFullInfo && pushEventType && typeOfModalWindow) {
			try {
				showLoader();
				await pushRestaurantNotification(restaurantForFullInfo.id, {
					eventType: pushEventType,
					forceReload,
					metadata: useDefaultTimeout ? undefined : { useExtendedTimeout: true },
				});
				changeTypeOfModalWindow('success');
			} catch (err) {
				changeTypeOfModalWindow('error');
			} finally {
				setForceReload(false);
				setUseDefaultTimeout(true);
				hideLoader();
			}
		}
	};

	const closeModal = () => {
		changeTypeOfModalWindow(null);
		setPushEventType(null);
		setForceReload(false);
		setUseDefaultTimeout(true);
	};

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={sendPush}
				onFailureEvent={closeModal}
				isVisible={typeOfModalWindow === 'confirm'}
				description={isSyncMenuPush ? 'syncMenuPushInfo' : 'pushInfo'}
				typeOfInfoIcon="toConfirmPush"
				additionalContent={
					<>
						{isForceReloadAvailable && (
							<PushOptionCheckbox
								checked={forceReload}
								onChange={setForceReload}
								translationKey="forcePushMediaContent"
								descriptionKey="forcePushMediaContentDescription"
								dataTest="ForceReloadCheckbox"
							/>
						)}
						{isExtendedTimeoutAvailable && (
							<PushOptionCheckbox
								checked={useDefaultTimeout}
								onChange={setUseDefaultTimeout}
								translationKey="useDefaultTimeout"
								dataTest="UseDefaultTimeoutCheckBox"
							/>
						)}
					</>
				}
			/>
			<ModalSentSuccessfully
				onConsentEvent={closeModal}
				isVisible={typeOfModalWindow === 'success'}
				description="pushSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={closeModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSendingPush"
			/>
		</div>
	);
};
